// SPDX-License-Identifier: AGPL-3.0-or-later

import type {FlatEmoji} from '@app/features/emoji/types/EmojiTypes';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {ComponentDispatch} from '@app/features/platform/utils/ComponentBus';
import {makeSyncedField} from '@app/features/user/state/SyncedField';
import {EmojiPickerStateSchema} from '@fluxer/schema/src/gen/fluxer/user/preferences/v1/pickers_pb';
import {makeAutoObservable} from 'mobx';

const logger = new Logger('EmojiPicker');

type EmojiUsageEntry = Readonly<{
	count: number;
	lastUsed: number;
}>;

const MAX_FRECENT_EMOJIS = 42;
const FRECENCY_TIME_DECAY_HOURS = 24 * 7;
const DEFAULT_QUICK_EMOJIS = [
	{name: 'thumbsup', uniqueName: 'thumbsup'},
	{name: 'ok_hand', uniqueName: 'ok_hand'},
	{name: 'tada', uniqueName: 'tada'},
	{name: 'heart', uniqueName: 'heart'},
];

class EmojiPicker {
	emojiUsage: Record<string, EmojiUsageEntry> = {};
	favoriteEmojis: Array<string> = [];
	collapsedCategories: Array<string> = [];
	private _favoriteSet: Set<string> = new Set();
	private _collapsedSet: Set<string> = new Set();

	constructor() {
		makeAutoObservable(
			this,
			{
				_favoriteSet: false,
				_collapsedSet: false,
			} as never,
			{autoBind: true},
		);
		void this.initPersistence();
	}

	private async initPersistence(): Promise<void> {
		await makeSyncedField(this, {
			field: 'emojiPicker',
			schema: EmojiPickerStateSchema,
			persist: ['emojiUsage', 'favoriteEmojis', 'collapsedCategories'],
			toMessage: (s) => ({
				usage: Object.fromEntries(
					Object.entries(s.emojiUsage).map(([key, entry]) => [
						key,
						{count: entry.count, lastUsedMs: BigInt(entry.lastUsed)},
					]),
				),
				favoriteEmojiIds: [...s.favoriteEmojis],
				collapsedCategoryIds: [...s.collapsedCategories],
			}),
			applyMessage: (s, m) => {
				const usage: Record<string, EmojiUsageEntry> = {};
				for (const [key, stat] of Object.entries(m.usage)) {
					usage[key] = {count: stat.count, lastUsed: Number(stat.lastUsedMs)};
				}
				s.emojiUsage = usage;
				s.favoriteEmojis = [...m.favoriteEmojiIds];
				s.collapsedCategories = [...m.collapsedCategoryIds];
				s._favoriteSet = new Set(s.favoriteEmojis);
				s._collapsedSet = new Set(s.collapsedCategories);
			},
			mergeForMigration: (local, server) => {
				const usage: Record<
					string,
					{
						count: number;
						lastUsedMs: bigint;
					}
				> = {};
				for (const [key, stat] of Object.entries(local.usage)) {
					usage[key] = {count: stat.count, lastUsedMs: stat.lastUsedMs};
				}
				for (const [key, stat] of Object.entries(server.usage)) {
					const existing = usage[key];
					if (existing === undefined) {
						usage[key] = {count: stat.count, lastUsedMs: stat.lastUsedMs};
					} else {
						usage[key] = {
							count: Math.max(existing.count, stat.count),
							lastUsedMs: existing.lastUsedMs > stat.lastUsedMs ? existing.lastUsedMs : stat.lastUsedMs,
						};
					}
				}
				const dedupedFavorites = Array.from(new Set([...local.favoriteEmojiIds, ...server.favoriteEmojiIds]));
				const dedupedCollapsed = Array.from(new Set([...local.collapsedCategoryIds, ...server.collapsedCategoryIds]));
				return {usage, favoriteEmojiIds: dedupedFavorites, collapsedCategoryIds: dedupedCollapsed};
			},
		});
		this._favoriteSet = new Set(this.favoriteEmojis);
		this._collapsedSet = new Set(this.collapsedCategories);
	}

	trackEmojiUsage(emojiKey: string): void {
		const now = Date.now();
		const currentUsage = this.emojiUsage[emojiKey];
		const newCount = (currentUsage?.count ?? 0) + 1;
		this.emojiUsage[emojiKey] = {
			count: newCount,
			lastUsed: now,
		};
		logger.debug(`Tracked emoji usage: ${emojiKey}`);
	}

	toggleFavorite(emojiKey: string): void {
		if (this._favoriteSet.has(emojiKey)) {
			this._favoriteSet.delete(emojiKey);
			const index = this.favoriteEmojis.indexOf(emojiKey);
			if (index > -1) this.favoriteEmojis.splice(index, 1);
		} else {
			this._favoriteSet.add(emojiKey);
			this.favoriteEmojis.push(emojiKey);
		}
		ComponentDispatch.dispatch('EMOJI_PICKER_RERENDER');
		logger.debug(`Toggled favorite emoji: ${emojiKey}`);
	}

	toggleCategory(category: string): void {
		if (this._collapsedSet.has(category)) {
			this._collapsedSet.delete(category);
			const index = this.collapsedCategories.indexOf(category);
			if (index > -1) this.collapsedCategories.splice(index, 1);
		} else {
			this._collapsedSet.add(category);
			this.collapsedCategories.push(category);
		}
		ComponentDispatch.dispatch('EMOJI_PICKER_RERENDER');
		logger.debug(`Toggled category: ${category}`);
	}

	isFavorite(emoji: FlatEmoji): boolean {
		void this.favoriteEmojis.length;
		return this._favoriteSet.has(this.getEmojiKey(emoji));
	}

	isCategoryCollapsed(categoryId: string): boolean {
		void this.collapsedCategories.length;
		return this._collapsedSet.has(categoryId);
	}

	private getFrecencyScore(entry: EmojiUsageEntry): number {
		const now = Date.now();
		const hoursSinceLastUse = (now - entry.lastUsed) / (1000 * 60 * 60);
		const timeDecay = Math.max(0, 1 - hoursSinceLastUse / FRECENCY_TIME_DECAY_HOURS);
		return entry.count * (1 + timeDecay);
	}

	getFrecentEmojis(allEmojis: ReadonlyArray<FlatEmoji>, limit: number = MAX_FRECENT_EMOJIS): Array<FlatEmoji> {
		const emojiScores: Array<{
			emoji: FlatEmoji;
			score: number;
		}> = [];
		for (const emoji of allEmojis) {
			const emojiKey = this.getEmojiKey(emoji);
			const usage = this.emojiUsage[emojiKey];
			if (usage) {
				const score = this.getFrecencyScore(usage);
				emojiScores.push({emoji, score});
			}
		}
		emojiScores.sort((a, b) => b.score - a.score);
		return emojiScores.slice(0, limit).map((item) => item.emoji);
	}

	getFavoriteEmojis(allEmojis: ReadonlyArray<FlatEmoji>): Array<FlatEmoji> {
		const favorites: Array<FlatEmoji> = [];
		for (const emoji of allEmojis) {
			if (this.isFavorite(emoji)) {
				favorites.push(emoji);
			}
		}
		return favorites;
	}

	getFrecencyScoreForEmoji(emoji: FlatEmoji): number {
		const usage = this.emojiUsage[this.getEmojiKey(emoji)];
		return usage ? this.getFrecencyScore(usage) : 0;
	}

	getFrecentEmojiKeys(limit = MAX_FRECENT_EMOJIS): Array<string> {
		const emojiScores: Array<{
			key: string;
			score: number;
		}> = [];
		for (const [key, usage] of Object.entries(this.emojiUsage)) {
			if (!usage) continue;
			emojiScores.push({key, score: this.getFrecencyScore(usage)});
		}
		emojiScores.sort((a, b) => b.score - a.score);
		return limit > 0 ? emojiScores.slice(0, limit).map((item) => item.key) : emojiScores.map((item) => item.key);
	}

	getDefaultQuickEmojiNames(count: number): Array<string> {
		return DEFAULT_QUICK_EMOJIS.slice(0, count).map((emoji) => emoji.uniqueName);
	}

	getQuickReactionEmojis(allEmojis: ReadonlyArray<FlatEmoji>, count: number): Array<FlatEmoji> {
		const frecent = this.getFrecentEmojis(allEmojis, count);
		if (frecent.length >= count) {
			return frecent.slice(0, count);
		}
		const result = [...frecent];
		const needed = count - frecent.length;
		const wantedNames = new Set<string>();
		for (let i = 0; i < needed && i < DEFAULT_QUICK_EMOJIS.length; i++) {
			wantedNames.add(DEFAULT_QUICK_EMOJIS[i].uniqueName);
		}
		const foundByName = new Map<string, FlatEmoji>();
		if (wantedNames.size > 0) {
			for (const e of allEmojis) {
				if (wantedNames.has(e.uniqueName) && !foundByName.has(e.uniqueName)) {
					foundByName.set(e.uniqueName, e);
					if (foundByName.size === wantedNames.size) break;
				}
			}
		}
		const seenKeys = new Set<string>();
		for (const r of result) seenKeys.add(this.getEmojiKey(r));
		for (let i = 0; i < needed && i < DEFAULT_QUICK_EMOJIS.length; i++) {
			const found = foundByName.get(DEFAULT_QUICK_EMOJIS[i].uniqueName);
			if (!found) continue;
			const key = this.getEmojiKey(found);
			if (seenKeys.has(key)) continue;
			seenKeys.add(key);
			result.push(found);
		}
		return result.slice(0, count);
	}

	private getEmojiKey(emoji: FlatEmoji): string {
		if (emoji.id) {
			return `custom:${emoji.guildId}:${emoji.id}`;
		}
		return `unicode:${emoji.uniqueName}`;
	}

	trackEmoji(emoji: FlatEmoji): void {
		this.trackEmojiUsage(this.getEmojiKey(emoji));
	}
}

export default new EmojiPicker();
